import React, { useState } from 'react';
import { Users, Mail, Phone, Linkedin } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

export interface HubTeamMember {
  id: string;
  name: string;
  title: string;
  department?: string | null;
  bio?: string | null;
  email?: string | null;
  phone?: string | null;
  photoUrl?: string | null;
  linkedinUrl?: string | null;
}

interface Props {
  members: HubTeamMember[];
  accent?: 'indigo' | 'blue';
}

export function HubTeamSection({ members, accent = 'indigo' }: Props) {
  const [selected, setSelected] = useState<HubTeamMember | null>(null);

  const visible = members.filter((m) => m.name !== 'Lisa Hewston');
  if (visible.length === 0) return null;

  const ring = accent === 'blue' ? 'ring-blue-100' : 'ring-indigo-100';
  const titleColor = accent === 'blue' ? 'text-blue-600' : 'text-indigo-600';
  const badgeBg = accent === 'blue' ? 'bg-blue-50 text-blue-600' : 'bg-indigo-50 text-indigo-600';
  const avatarBg = accent === 'blue' ? 'bg-blue-500' : 'bg-gradient-to-br from-indigo-500 to-indigo-600';
  const headerIcon = accent === 'blue' ? 'text-blue-600' : 'text-indigo-600';

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-2">
            <Users className={`w-4 h-4 ${headerIcon}`} />
            <h2 className="font-semibold text-sm text-gray-800">Our Team</h2>
            <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${badgeBg}`}>{visible.length}</span>
          </div>
        </div>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {visible.map((member) => (
            <button
              key={member.id}
              type="button"
              onClick={() => setSelected(member)}
              className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100 hover:border-gray-200 hover:bg-white hover:shadow-sm transition-all text-left w-full cursor-pointer"
            >
              {member.photoUrl ? (
                <img
                  src={member.photoUrl}
                  alt={member.name}
                  className={`w-10 h-10 rounded-full object-cover flex-shrink-0 ring-2 ${ring}`}
                />
              ) : (
                <div
                  className={`w-10 h-10 rounded-full ${avatarBg} flex items-center justify-center flex-shrink-0 text-white text-sm font-bold`}
                >
                  {member.name
                    .split(' ')
                    .map((w) => w[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate">{member.name}</p>
                <p className={`text-xs font-semibold truncate ${titleColor}`}>{member.title}</p>
                {member.department && (
                  <span className="inline-block mt-0.5 text-xs text-gray-500 bg-white border border-gray-200 px-1.5 py-0.5 rounded-full">
                    {member.department}
                  </span>
                )}
                <p className="text-xs text-gray-400 mt-1">View profile</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="sm:max-w-md">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-left pr-8">{selected.name}</DialogTitle>
              </DialogHeader>
              <div className="flex items-start gap-4">
                {selected.photoUrl ? (
                  <img
                    src={selected.photoUrl}
                    alt={selected.name}
                    className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div
                    className={`w-16 h-16 rounded-full ${avatarBg} flex items-center justify-center flex-shrink-0 text-white text-lg font-bold`}
                  >
                    {selected.name
                      .split(' ')
                      .map((w) => w[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium ${titleColor}`}>{selected.title}</p>
                  {selected.department && (
                    <span className="inline-block mt-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                      {selected.department}
                    </span>
                  )}
                </div>
              </div>
              {selected.bio && (
                <p className="text-sm text-gray-600 leading-relaxed mt-2">{selected.bio}</p>
              )}
              <div className="flex flex-wrap gap-3 pt-3 border-t border-gray-100">
                {selected.email && (
                  <a
                    href={`mailto:${selected.email}`}
                    className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-600"
                  >
                    <Mail className="w-4 h-4" /> {selected.email}
                  </a>
                )}
                {selected.phone && (
                  <a
                    href={`tel:${selected.phone}`}
                    className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-600"
                  >
                    <Phone className="w-4 h-4" /> {selected.phone}
                  </a>
                )}
                {selected.linkedinUrl && (
                  <a
                    href={selected.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-600"
                  >
                    <Linkedin className="w-4 h-4" /> LinkedIn
                  </a>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
